/**
 * Home Page Email Signup Modal - Simple Version
 * Handles modal show/hide functionality only
 * Content is rendered directly in Liquid template
 */

class HomeEmailSignupModal {
  constructor() {
    this.modal = null;
    this.isModalShown = false;
    this.isShowingModal = false;
    this.timeoutId = null;
    this.scrollTriggered = false;
    this.timeTriggered = false;
    this.sessionKey = "homeEmailModalShown";

    this.init();
  }

  init() {
    // Check if modal was already shown in this session
    if (sessionStorage.getItem(this.sessionKey)) {
      console.log("Home email modal already shown in this session");
      return;
    }

    console.log("Home email modal initializing...");

    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setupModal());
    } else {
      this.setupModal();
    }
  }

  setupModal() {
    console.log("Setting up home email modal...");

    // Find the modal element (rendered by Liquid)
    this.modal = document.getElementById("home-email-signup-modal");

    if (!this.modal) {
      console.error("Home email modal element not found");
      return;
    }

    // Set up triggers
    this.setupTimeTrigger();
    this.setupScrollTrigger();

    // Set up close functionality
    this.setupCloseHandlers();

    console.log("Home email modal setup complete");
  }

  setupTimeTrigger() {
    // Random delay between 5-7 seconds
    const delay = Math.random() * 2000 + 5000; // 5000-7000ms
    console.log(`Home email modal time trigger set for ${delay}ms`);

    this.timeoutId = setTimeout(() => {
      if (!this.isModalShown && !this.scrollTriggered && !this.isShowingModal) {
        console.log("Home email modal triggered by time");
        this.timeTriggered = true;
        this.showModal();
      }
    }, delay);
  }

  setupScrollTrigger() {
    let scrollHandler;

    scrollHandler = () => {
      if (
        this.isModalShown ||
        this.scrollTriggered ||
        this.timeTriggered ||
        this.isShowingModal
      ) {
        window.removeEventListener("scroll", scrollHandler);
        return;
      }

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / documentHeight) * 100;

      if (scrollPercent >= 50) {
        console.log(
          `Home email modal triggered by scroll at ${scrollPercent}%`
        );
        this.scrollTriggered = true;
        this.showModal();
        window.removeEventListener("scroll", scrollHandler);
      }
    };

    window.addEventListener("scroll", scrollHandler, { passive: true });
  }

  setupCloseHandlers() {
    if (!this.modal) return;

    // Close button
    const closeButton = this.modal.querySelector(
      ".home-email-signup-modal__toggle"
    );
    if (closeButton) {
      closeButton.addEventListener("click", () => this.hideModal());
    }

    // Click outside to close
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) {
        this.hideModal();
      }
    });

    // Escape key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isModalShown) {
        this.hideModal();
      }
    });
  }

  showModal() {
    console.log("Home email modal showModal called");

    if (this.isModalShown || this.isShowingModal || !this.modal) {
      console.log(
        "Home email modal already shown, already showing, or modal not found"
      );
      return;
    }

    // Set flag to prevent concurrent calls
    this.isShowingModal = true;

    // Clear timeout if it exists
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    console.log("Showing modal...");
    // Show modal
    this.modal.setAttribute("open", "");
    this.modal.style.display = "flex";
    this.isModalShown = true;
    this.isShowingModal = false;

    // Mark as shown in session storage
    sessionStorage.setItem(this.sessionKey, "true");

    // Focus management
    const firstFocusable = this.modal.querySelector(
      'input, button, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = "hidden";

    // Analytics tracking (optional)
    if (typeof gtag !== "undefined") {
      gtag("event", "home_email_signup_modal_shown", {
        trigger_type: this.timeTriggered ? "time" : "scroll",
        scroll_percent: this.scrollTriggered ? this.getScrollPercent() : null,
      });
    }
  }

  hideModal() {
    if (!this.isModalShown || !this.modal) return;

    this.modal.removeAttribute("open");
    this.modal.style.display = "none";
    this.isModalShown = false;

    // Restore body scroll
    document.body.style.overflow = "";

    // Analytics tracking (optional)
    if (typeof gtag !== "undefined") {
      gtag("event", "home_email_signup_modal_closed");
    }
  }

  getScrollPercent() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    return Math.round((scrollTop / documentHeight) * 100);
  }

  // Public method to manually show modal (for testing)
  show() {
    console.log("Manually showing home email modal");
    this.showModal();
  }

  // Public method to manually hide modal
  hide() {
    console.log("Manually hiding home email modal");
    this.hideModal();
  }

  // Test method to reset session and show modal
  test() {
    console.log(
      "Testing home email modal - clearing session and showing modal"
    );
    sessionStorage.removeItem(this.sessionKey);
    this.isModalShown = false;
    this.showModal();
  }
}

// Initialize the modal when the script loads
const homeEmailSignupModal = new HomeEmailSignupModal();

// Make it globally available for testing
window.homeEmailSignupModal = homeEmailSignupModal;
